import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Header } from '../../components/Header';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import { toast } from '../../lib/toast';
import {
  Plus,
  MoreVertical,
  UserCheck,
  UserX,
  KeyRound,
  Trash2,
  FolderOpen,
  Users,
  UserPlus,
  CheckCircle2,
  Circle,
  Loader2,
  ShieldOff,
  Eye,
  Pencil,
} from 'lucide-react';
import * as colaboradorService from '../../services/colaborador.service';
import type { Colaborador, ColaboradorProjeto } from '../../services/colaborador.service';
import * as projectService from '../../services/project.service';
import type { Project } from '../../services/project.service';
import { usePageTitle } from '../../hooks/usePageTitle';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const AVATAR_COLORS = [
  'bg-blue-100 text-blue-700',
  'bg-violet-100 text-violet-700',
  'bg-emerald-100 text-emerald-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
];

function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(nome: string): string {
  const parts = nome.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  return nome.substring(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Tipos locais
// ---------------------------------------------------------------------------
interface NovoColaboradorForm {
  nome: string;
  email: string;
  senha: string;
}

interface AcessoProjetosState {
  colaborador: Colaborador;
  projetosVinculados: ColaboradorProjeto[];
}

type PermissaoProjeto = 'view' | 'edit';

// ---------------------------------------------------------------------------
// Skeleton de carregamento
// ---------------------------------------------------------------------------
function ColaboradorSkeleton() {
  return (
    <div className="rounded-xl border border-border bg-card p-4 shadow-sm space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-10 w-10 rounded-full" />
        <div className="flex-1 space-y-2">
          <Skeleton className="h-4 w-36" />
          <Skeleton className="h-3 w-48" />
        </div>
        <Skeleton className="h-6 w-14 rounded-full" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// PÃ¡gina principal
// ---------------------------------------------------------------------------
export default function EquipePage() {
  const { t } = useTranslation('common');
  usePageTitle(t('equipe.title'));
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (user && user.account_type === 'colaborador') {
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);

  const [colaboradores, setColaboradores] = useState<Colaborador[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal: criar
  const [showCriar, setShowCriar] = useState(false);
  const [criando, setCriando] = useState(false);
  const [novoForm, setNovoForm] = useState<NovoColaboradorForm>({ nome: '', email: '', senha: '' });
  const [projetosSelecionadosNovo, setProjetosSelecionadosNovo] = useState<Record<number, PermissaoProjeto>>({});
  const [todosProjetosNovo, setTodosProjetosNovo] = useState<Project[]>([]);
  const [carregandoProjetosNovo, setCarregandoProjetosNovo] = useState(false);

  // Modal: redefinir senha
  const [resetSenha, setResetSenha] = useState<Colaborador | null>(null);
  const [novaSenha, setNovaSenha] = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  // Alert: confirmar remoÃ§Ã£o
  const [removerAlvo, setRemoverAlvo] = useState<Colaborador | null>(null);
  const [removendo, setRemovendo] = useState(false);

  // Modal: projetos
  const [acessoProjetos, setAcessoProjetos] = useState<AcessoProjetosState | null>(null);
  const [todosOsProjetos, setTodosOsProjetos] = useState<Project[]>([]);
  const [salvandoAcesso, setSalvandoAcesso] = useState<number | null>(null);
  const [salvandoPermissao, setSalvandoPermissao] = useState<number | null>(null);
  const [carregandoProjetos, setCarregandoProjetos] = useState(false);

  // ---------------------------------------------------------------------------
  const carregarColaboradores = useCallback(async () => {
    try {
      setLoading(true);
      const data = await colaboradorService.listarColaboradores();
      setColaboradores(data);
    } catch {
      toast.error(t('equipe.error_load'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.account_type !== 'colaborador') carregarColaboradores();
  }, [carregarColaboradores, user]);

  const abrirCriarColaborador = async () => {
    setNovoForm({ nome: '', email: '', senha: '' });
    setProjetosSelecionadosNovo({});
    setShowCriar(true);
    setCarregandoProjetosNovo(true);
    try {
      const todos = await projectService.getUserProjects();
      setTodosProjetosNovo(todos);
    } catch {
      setTodosProjetosNovo([]);
    } finally {
      setCarregandoProjetosNovo(false);
    }
  };

  // Stats
  const totalAtivos = colaboradores.filter((c) => c.ativo).length;
  const totalInativos = colaboradores.length - totalAtivos;

  // ---------------------------------------------------------------------------
  // Criar
  // ---------------------------------------------------------------------------
  const handleCriar = async () => {
    if (!novoForm.nome.trim() || !novoForm.email.trim() || !novoForm.senha.trim()) {
      toast.error(t('equipe.error_fill_all'));
      return;
    }
    try {
      setCriando(true);
      const criado = await colaboradorService.criarColaborador(novoForm);
      setColaboradores((prev) => [...prev, criado]);

      // Vincular projetos selecionados com suas permissões
      const entries = Object.entries(projetosSelecionadosNovo);
      if (entries.length > 0) {
        await Promise.all(
          entries.map(([projetoId, permissao]) =>
            colaboradorService.vincularProjeto(criado.id, Number(projetoId), permissao)
          )
        );
      }

      setShowCriar(false);
      setNovoForm({ nome: '', email: '', senha: '' });
      setProjetosSelecionadosNovo({});
      toast.success(t('equipe.created_success', { nome: criado.nome }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? t('equipe.error_create'));
    } finally {
      setCriando(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Toggle ativo
  // ---------------------------------------------------------------------------
  const handleToggleAtivo = async (colaborador: Colaborador) => {
    try {
      const atualizado = await colaboradorService.atualizarColaborador(colaborador.id, {
        ativo: !colaborador.ativo,
      });
      setColaboradores((prev) =>
        prev.map((c) => (c.id === colaborador.id ? { ...c, ativo: atualizado.ativo } : c))
      );
      toast.success(atualizado.ativo ? t('equipe.activated') : t('equipe.deactivated'));
    } catch {
      toast.error(t('equipe.error_status'));
    }
  };

  // ---------------------------------------------------------------------------
  // Redefinir senha
  // ---------------------------------------------------------------------------
  const handleResetSenha = async () => {
    if (!novaSenha || novaSenha.length < 6) {
      toast.error(t('equipe.password_min_length'));
      return;
    }
    try {
      setSalvandoSenha(true);
      await colaboradorService.redefinirSenha(resetSenha!.id, novaSenha);
      setResetSenha(null);
      setNovaSenha('');
      toast.success(t('equipe.password_reset_success'));
    } catch {
      toast.error(t('equipe.error_reset_password'));
    } finally {
      setSalvandoSenha(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Remover
  // ---------------------------------------------------------------------------
  const handleConfirmarRemover = async () => {
    if (!removerAlvo) return;
    try {
      setRemovendo(true);
      await colaboradorService.removerColaborador(removerAlvo.id);
      setColaboradores((prev) => prev.filter((c) => c.id !== removerAlvo.id));
      toast.success(t('equipe.removed'));
      setRemoverAlvo(null);
    } catch {
      toast.error(t('equipe.error_remove'));
    } finally {
      setRemovendo(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Projetos
  // ---------------------------------------------------------------------------
  const abrirGerenciarProjetos = async (colaborador: Colaborador) => {
    setCarregandoProjetos(true);
    setAcessoProjetos({ colaborador, projetosVinculados: [] });
    try {
      const [vinculados, todos] = await Promise.all([
        colaboradorService.listarProjetosColaborador(colaborador.id),
        projectService.getUserProjects(),
      ]);
      setAcessoProjetos({ colaborador, projetosVinculados: vinculados });
      setTodosOsProjetos(todos);
    } catch {
      toast.error(t('equipe.error_load_projects'));
      setAcessoProjetos(null);
    } finally {
      setCarregandoProjetos(false);
    }
  };

  const isVinculado = (projetoId: number) =>
    acessoProjetos?.projetosVinculados.some((p) => p.id === projetoId) ?? false;

  const handleToggleProjeto = async (projeto: Project) => {
    if (!acessoProjetos) return;
    const id = Number(projeto.id);
    try {
      setSalvandoAcesso(id);
      if (isVinculado(id)) {
        await colaboradorService.desvincularProjeto(acessoProjetos.colaborador.id, id);
        setAcessoProjetos((prev) =>
          prev ? { ...prev, projetosVinculados: prev.projetosVinculados.filter((p) => p.id !== id) } : prev
        );
      } else {
        await colaboradorService.vincularProjeto(acessoProjetos.colaborador.id, id, 'view');
        setAcessoProjetos((prev) =>
          prev ? { ...prev, projetosVinculados: [...prev.projetosVinculados, { id, nome: projeto.name, permissao: 'view' }] } : prev
        );
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? t('equipe.error_update_access'));
    } finally {
      setSalvandoAcesso(null);
    }
  };

  const handleUpdatePermissao = async (projetoId: number, permissao: PermissaoProjeto) => {
    if (!acessoProjetos) return;
    try {
      setSalvandoPermissao(projetoId);
      await colaboradorService.atualizarPermissaoProjeto(acessoProjetos.colaborador.id, projetoId, permissao);
      setAcessoProjetos((prev) =>
        prev
          ? {
              ...prev,
              projetosVinculados: prev.projetosVinculados.map((p) =>
                p.id === projetoId ? { ...p, permissao } : p
              ),
            }
          : prev
      );
    } catch {
      toast.error(t('equipe.error_update_permission'));
    } finally {
      setSalvandoPermissao(null);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <TooltipProvider>
      <div className="min-h-screen bg-background">
        <Header
          breadcrumbs={[
            { label: 'Dashboard', href: '/dashboard' },
            { label: t('equipe.title') },
          ]}
        />

        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-8">

          {/* ---------------------------------------------------------------- */}
          {/* CabeÃ§alho                                                        */}
          {/* ---------------------------------------------------------------- */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => navigate('/dashboard')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
              </Button>
              <div>
                <h1 className="text-2xl font-bold tracking-tight">{t('equipe.title')}</h1>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {t('equipe.subtitle')}
                </p>
              </div>
            </div>
            <Button onClick={abrirCriarColaborador} className="shrink-0">
              <UserPlus className="h-4 w-4 mr-2" />
              {t('equipe.new_collaborator')}
            </Button>
          </div>

          {/* ---------------------------------------------------------------- */}
          {/* Cards de stats                                                   */}
          {/* ---------------------------------------------------------------- */}
          {!loading && colaboradores.length > 0 && (
            <div className="grid grid-cols-3 gap-3">
              {[
                { label: t('equipe.stats_total'), value: colaboradores.length, color: 'text-foreground' },
                { label: t('equipe.stats_active'), value: totalAtivos, color: 'text-emerald-600' },
                { label: t('equipe.stats_inactive'), value: totalInativos, color: 'text-muted-foreground' },
              ].map(({ label, value, color }) => (
                <div key={label} className="rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{label}</p>
                  <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* ---------------------------------------------------------------- */}
          {/* Lista                                                            */}
          {/* ---------------------------------------------------------------- */}
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => <ColaboradorSkeleton key={i} />)}
            </div>
          ) : colaboradores.length === 0 ? (
            <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border bg-card py-16 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
                <Users className="h-7 w-7 text-muted-foreground" />
              </div>
              <p className="text-base font-semibold">{t('equipe.empty_title')}</p>
              <p className="text-sm text-muted-foreground mt-1 mb-5 max-w-xs">
                {t('equipe.empty_description')}
              </p>
              <Button onClick={abrirCriarColaborador} variant="outline" size="sm">
                <UserPlus className="h-4 w-4 mr-2" />
                {t('equipe.create_first')}
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {colaboradores.map((colaborador) => {
                const initials = getInitials(colaborador.nome || colaborador.email);
                const avatarColor = getAvatarColor(colaborador.nome || colaborador.email);

                return (
                  <div
                    key={colaborador.id}
                    className={`group flex items-center gap-4 rounded-xl border border-border bg-card px-4 py-3 shadow-sm transition-all hover:shadow-md ${
                      !colaborador.ativo ? 'opacity-60' : ''
                    }`}
                  >
                    {/* Avatar */}
                    <div
                      className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-semibold text-sm ${avatarColor}`}
                    >
                      {initials}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium truncate leading-tight">{colaborador.nome}</p>
                        {!colaborador.ativo && (
                          <span className="text-xs text-muted-foreground italic">{t('equipe.inactive_label')}</span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">{colaborador.email}</p>
                    </div>

                    {/* Status pill */}
                    <div className="hidden sm:flex items-center gap-1.5 shrink-0">
                      <span
                        className={`h-2 w-2 rounded-full ${colaborador.ativo ? 'bg-emerald-500' : 'bg-gray-300'}`}
                      />
                      <span className="text-xs text-muted-foreground">
                        {colaborador.ativo ? t('equipe.status_active') : t('equipe.status_inactive')}
                      </span>
                    </div>

                    {/* AÃ§Ãµes rÃ¡pidas */}
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => abrirGerenciarProjetos(colaborador)}
                          >
                            <FolderOpen className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('equipe.manage_projects')}</TooltipContent>
                      </Tooltip>

                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => { setResetSenha(colaborador); setNovaSenha(''); }}
                          >
                            <KeyRound className="h-4 w-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('equipe.reset_password')}</TooltipContent>
                      </Tooltip>
                    </div>

                    {/* Menu contextual */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-48">
                        <DropdownMenuItem onClick={() => abrirGerenciarProjetos(colaborador)}>
                          <FolderOpen className="h-4 w-4 mr-2" />
                          {t('equipe.manage_projects')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setResetSenha(colaborador); setNovaSenha(''); }}>
                          <KeyRound className="h-4 w-4 mr-2" />
                          {t('equipe.reset_password')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => handleToggleAtivo(colaborador)}>
                          {colaborador.ativo ? (
                            <><UserX className="h-4 w-4 mr-2" />{t('equipe.deactivate_access')}</>
                          ) : (
                            <><UserCheck className="h-4 w-4 mr-2" />{t('equipe.activate_access')}</>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => setRemoverAlvo(colaborador)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          {t('equipe.remove_collaborator')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                );
              })}
            </div>
          )}
        </main>

        {/* ------------------------------------------------------------------ */}
        {/* Modal: criar colaborador                                            */}
        {/* ------------------------------------------------------------------ */}
        <Dialog open={showCriar} onOpenChange={(open) => { if (!criando) { setShowCriar(open); if (!open) { setProjetosSelecionadosNovo({}); } } }}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{t('equipe.new_collaborator')}</DialogTitle>
              <DialogDescription>
                {t('equipe.create_description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-1.5">
                <Label htmlFor="novo-nome">{t('equipe.field_full_name')}</Label>
                <Input
                  id="novo-nome"
                  placeholder="Ex: Maria Silva"
                  value={novoForm.nome}
                  onChange={(e) => setNovoForm((f) => ({ ...f, nome: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="novo-email">{t('equipe.field_access_email')}</Label>
                <Input
                  id="novo-email"
                  type="email"
                  placeholder="colaborador@empresa.com"
                  value={novoForm.email}
                  onChange={(e) => setNovoForm((f) => ({ ...f, email: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="novo-senha">{t('equipe.field_initial_password')}</Label>
                <Input
                  id="novo-senha"
                  type="password"
                  placeholder={t('equipe.password_placeholder')}
                  value={novoForm.senha}
                  onChange={(e) => setNovoForm((f) => ({ ...f, senha: e.target.value }))}
                />
                <p className="text-xs text-muted-foreground">
                  {t('equipe.password_hint')}
                </p>
              </div>

              {/* Seleção de projetos */}
              <div className="space-y-1.5">
                <Label>{t('equipe.projects_access')} <span className="text-muted-foreground font-normal">({t('optional')})</span></Label>
                {carregandoProjetosNovo ? (
                  <div className="space-y-1.5">
                    {[1, 2].map((i) => <Skeleton key={i} className="h-10 rounded-lg" />)}
                  </div>
                ) : todosProjetosNovo.length === 0 ? (
                  <p className="text-xs text-muted-foreground py-2">{t('equipe.no_projects')}</p>
                ) : (
                  <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1 rounded-lg border border-border bg-muted/30 p-2">
                    {todosProjetosNovo.map((projeto) => {
                      const id = Number(projeto.id);
                      const selecionado = id in projetosSelecionadosNovo;
                      const permissao = projetosSelecionadosNovo[id] ?? 'view';

                      return (
                        <div
                          key={id}
                          className={`flex items-center gap-2 rounded-lg border px-3 py-2 transition-all ${
                            selecionado
                              ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40'
                              : 'border-border bg-background hover:bg-muted/50'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setProjetosSelecionadosNovo((prev) => {
                                if (id in prev) {
                                  const next = { ...prev };
                                  delete next[id];
                                  return next;
                                }
                                return { ...prev, [id]: 'view' };
                              });
                            }}
                            className="flex items-center gap-2 flex-1 min-w-0 text-left"
                          >
                            {selecionado ? (
                              <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                            ) : (
                              <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                            )}
                            <span className={`text-sm font-medium truncate ${selecionado ? 'text-emerald-700 dark:text-emerald-300' : ''}`}>
                              {projeto.name}
                            </span>
                          </button>

                          {selecionado && (
                            <div className="flex items-center gap-1 shrink-0">
                              <button
                                type="button"
                                onClick={() => setProjetosSelecionadosNovo((prev) => ({ ...prev, [id]: 'view' }))}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                  permissao === 'view'
                                    ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                    : 'text-muted-foreground hover:bg-muted'
                                }`}
                              >
                                <Eye className="h-3 w-3" />
                                {t('equipe.permission_view')}
                              </button>
                              <button
                                type="button"
                                onClick={() => setProjetosSelecionadosNovo((prev) => ({ ...prev, [id]: 'edit' }))}
                                className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                  permissao === 'edit'
                                    ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                    : 'text-muted-foreground hover:bg-muted'
                                }`}
                              >
                                <Pencil className="h-3 w-3" />
                                {t('equipe.permission_edit')}
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
                {Object.keys(projetosSelecionadosNovo).length > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {t('equipe.projects_selected', { count: Object.keys(projetosSelecionadosNovo).length })}
                  </p>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setShowCriar(false); setProjetosSelecionadosNovo({}); }} disabled={criando}>
                {t('cancel')}
              </Button>
              <Button onClick={handleCriar} disabled={criando}>
                {criando ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('equipe.creating')}</>
                ) : (
                  <><UserPlus className="h-4 w-4 mr-2" />{t('equipe.create_collaborator')}</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ------------------------------------------------------------------ */}
        {/* Modal: redefinir senha                                              */}
        {/* ------------------------------------------------------------------ */}
        <Dialog open={!!resetSenha} onOpenChange={(open) => !open && setResetSenha(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <KeyRound className="h-4 w-4" />
                {t('equipe.reset_password')}
              </DialogTitle>
              <DialogDescription>
                {t('equipe.reset_for', { nome: resetSenha?.nome, email: resetSenha?.email })}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5 py-2">
              <Label htmlFor="nova-senha">{t('equipe.new_password')}</Label>
              <Input
                id="nova-senha"
                type="password"
                placeholder={t('equipe.password_placeholder')}
                value={novaSenha}
                onChange={(e) => setNovaSenha(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleResetSenha()}
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setResetSenha(null)} disabled={salvandoSenha}>
                {t('cancel')}
              </Button>
              <Button onClick={handleResetSenha} disabled={salvandoSenha}>
                {salvandoSenha ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('saving')}</>
                ) : (
                  t('equipe.reset_password')
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ------------------------------------------------------------------ */}
        {/* AlertDialog: confirmar remoÃ§Ã£o                                      */}
        {/* ------------------------------------------------------------------ */}
        <AlertDialog open={!!removerAlvo} onOpenChange={(open) => !open && setRemoverAlvo(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('equipe.remove_title')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('equipe.remove_description', { nome: removerAlvo?.nome })}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removendo}>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={handleConfirmarRemover}
                disabled={removendo}
              >
                {removendo ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('equipe.removing')}</>
                ) : (
                  t('equipe.confirm_remove')
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* ------------------------------------------------------------------ */}
        {/* Modal: gerenciar projetos                                           */}
        {/* ------------------------------------------------------------------ */}
        <Dialog open={!!acessoProjetos} onOpenChange={(open) => !open && setAcessoProjetos(null)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FolderOpen className="h-4 w-4" />
                {t('equipe.projects_access_title')}
              </DialogTitle>
              {acessoProjetos && (
                <DialogDescription>
                  {t('equipe.projects_description', { nome: acessoProjetos.colaborador.nome })}
                </DialogDescription>
              )}
            </DialogHeader>

            <div className="py-2">
              {carregandoProjetos ? (
                <div className="space-y-2">
                  {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 rounded-lg" />)}
                </div>
              ) : todosOsProjetos.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ShieldOff className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">{t('equipe.no_projects_registered')}</p>
                </div>
              ) : (
                <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {todosOsProjetos.map((projeto) => {
                    const id = Number(projeto.id);
                    const vinculado = isVinculado(id);
                    const carregando = salvandoAcesso === id;
                    const atualizandoPerm = salvandoPermissao === id;
                    const projetoVinculado = acessoProjetos?.projetosVinculados.find((p) => p.id === id);
                    const permissao = projetoVinculado?.permissao ?? 'view';

                    return (
                      <div
                        key={id}
                        className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 transition-all ${
                          vinculado
                            ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-950/40'
                            : 'border-border hover:bg-muted/50'
                        } ${carregando ? 'opacity-60' : ''}`}
                      >
                        <button
                          onClick={() => handleToggleProjeto(projeto)}
                          disabled={carregando || atualizandoPerm}
                          className="flex items-center gap-2 flex-1 min-w-0 text-left"
                        >
                          {carregando ? (
                            <Loader2 className="h-4 w-4 animate-spin shrink-0 text-muted-foreground" />
                          ) : vinculado ? (
                            <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                          ) : (
                            <Circle className="h-4 w-4 shrink-0 text-muted-foreground/40" />
                          )}
                          <span className={`text-sm font-medium truncate ${vinculado ? 'text-emerald-700 dark:text-emerald-300' : ''}`}>
                            {projeto.name}
                          </span>
                        </button>

                        {vinculado && (
                          <div className="flex items-center gap-1 shrink-0">
                            {atualizandoPerm ? (
                              <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />
                            ) : (
                              <>
                                <button
                                  onClick={() => handleUpdatePermissao(id, 'view')}
                                  disabled={carregando}
                                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                    permissao === 'view'
                                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300'
                                      : 'text-muted-foreground hover:bg-muted'
                                  }`}
                                >
                                  <Eye className="h-3 w-3" />
                                  {t('equipe.permission_view')}
                                </button>
                                <button
                                  onClick={() => handleUpdatePermissao(id, 'edit')}
                                  disabled={carregando}
                                  className={`flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition-colors ${
                                    permissao === 'edit'
                                      ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                      : 'text-muted-foreground hover:bg-muted'
                                  }`}
                                >
                                  <Pencil className="h-3 w-3" />
                                  {t('equipe.permission_edit')}
                                </button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {acessoProjetos && todosOsProjetos.length > 0 && (
                <p className="text-xs text-muted-foreground mt-3 text-center">
                  {t('equipe.projects_with_access', { count: acessoProjetos.projetosVinculados.length, total: todosOsProjetos.length })}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button onClick={() => setAcessoProjetos(null)}>{t('close')}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </TooltipProvider>
  );
}
